import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import moment from 'moment'
import { toast } from 'react-toastify'
import Drawer from 'components/Drawer'
import PageLoading from 'components/PageLoading'
import ParagraphInput from 'components/ParagraphInput'
import PersonName from 'components/PersonName'
import { Select } from 'components/Select'
import { IOptions } from 'components/Select/help'
import RecordingPlayer from './RecordingPlayer'
import {
  getAiCallLogs,
  updateAiCall,
} from 'redux/aiCalls'
import {
  AI_CALL_EDIT_STATUS_OPTIONS,
  getPersonAvatar,
  getPersonDisplayName,
  getStatusText,
} from 'pages/AiCalls/helpers/constant'

export type TAiCallDrawerProp = {
  open: boolean
  ticket: Record<string, any>
  assigneeOptions: IOptions[]
  toggle: () => void
  onUpdated: (ticket?: Record<string, any>) => void
}

type TEditForm = {
  status: string
  assignee_crm_user_id: string
  note: string
}

const UNASSIGNED_VALUE = 'none'

const Field = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) => {
  return (
    <div>
      <div className='text-xs text-light-300 mb-1'>{label}</div>
      <div className='text-sm text-light-700 leading-6 break-words'>
        {children || '-'}
      </div>
    </div>
  )
}

const renderValue = (value: any) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }
  return value
}

const TextBlock = ({ label, value }: { label: string; value?: any }) => {
  return (
    <div className='border border-dark-100 rounded-lg p-4'>
      <div className='text-xs text-light-300 mb-2'>{label}</div>
      <div className='text-sm text-light-700 leading-6 whitespace-pre-wrap break-words'>
        {renderValue(value)}
      </div>
    </div>
  )
}

const formatDate = (value?: string) => {
  if (!value) {
    return '-'
  }
  return moment(value).format('MMM DD, YYYY h:mm A')
}

const formatPhone = (value?: string) => {
  if (!value) {
    return '-'
  }

  const digits = value.replace(/\D/g, '')
  const phone = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits

  if (phone.length !== 10) {
    return value
  }

  return `(${phone.slice(0, 3)})${phone.slice(3, 6)}-${phone.slice(6)}`
}

const formatValue = (value: any) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return getStatusText(String(value))
}

const formatLogField = (key: string) => {
  if (key === 'assignee_crm_user_id') {
    return 'Assignee'
  }

  return key
}

export default function AiCallDrawer({
  open,
  ticket,
  assigneeOptions,
  toggle,
  onUpdated,
}: TAiCallDrawerProp): JSX.Element {
  const [logs, setLogs] = useState<any[]>([])
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    status: '',
    assignee_crm_user_id: '',
    note: '',
  })
  const [initialForm, setInitialForm] = useState<TEditForm>({
    status: '',
    assignee_crm_user_id: '',
    note: '',
  })

  const editAssigneeOptions = [
    {
      label: 'None',
      value: UNASSIGNED_VALUE,
      img_url: '',
    },
    ...assigneeOptions.filter(item => item.value),
  ]
  const formatLogValue = (key: string, value: any) => {
    if (key === 'assignee_crm_user_id') {
      const option = assigneeOptions.find(item => String(item.value) === String(value))
      return option?.label || formatValue(value)
    }

    return formatValue(value)
  }

  const loadLogs = async () => {
    if (!ticket?.id) {
      return
    }
    setLoadingLogs(true)
    try {
      const res = await getAiCallLogs(ticket.id)
      setLogs(res?.data || res || [])
    } catch (e: any) {
      if (e && e.message) {
        toast.error(e.message)
      }
    }
    setLoadingLogs(false)
  }

  const handleSave = async () => {
    if (!ticket?.id) {
      return
    }

    const hasChanges =
      form.status !== initialForm.status ||
      form.assignee_crm_user_id !== initialForm.assignee_crm_user_id ||
      form.note !== initialForm.note

    if (!hasChanges) {
      return
    }

    setSaving(true)
    try {
      const payload: Record<string, string | null> = {}

      if (form.status !== initialForm.status) {
        payload.status = form.status
      }

      if (form.assignee_crm_user_id !== initialForm.assignee_crm_user_id) {
        payload.assignee_crm_user_id =
          form.assignee_crm_user_id === UNASSIGNED_VALUE
            ? null
            : form.assignee_crm_user_id
      }

      if (form.note !== initialForm.note) {
        payload.note = form.note
      }

      const res = await updateAiCall(ticket.id, payload)
      toast.success('AI call updated')
      setInitialForm(form)
      await loadLogs()
      onUpdated(res?.data || res)
    } catch (e: any) {
      if (e && e.message) {
        toast.error(e.message)
      }
    }
    setSaving(false)
  }

  useEffect(() => {
    if (open && ticket?.id) {
      const nextForm = {
        status: ticket.status || '',
        assignee_crm_user_id: ticket.assignee_crm_user_id
          ? String(ticket.assignee_crm_user_id)
          : UNASSIGNED_VALUE,
        note: ticket.note || '',
      }

      setForm(nextForm)
      setInitialForm(nextForm)
      loadLogs()
    }
  }, [open, ticket?.id])

  const hasChanges =
    form.status !== initialForm.status ||
    form.assignee_crm_user_id !== initialForm.assignee_crm_user_id ||
    form.note !== initialForm.note

  return (
    <Drawer
      className='w-[760px]'
      title='AI CALL'
      open={open}
      toggle={toggle}
      okText='SAVE'
      okButtonProps={{ loading: saving, disabled: !hasChanges }}
      onOk={handleSave}
      onCancel={toggle}
    >
      <div className='space-y-8'>
        <div>
          <div>
            <div className='text-xl text-light-700 font-bold'>
              {ticket?.brand_name || 'AI call'}
            </div>
            <div className='text-sm text-light-300 mt-1'>
              AI Call #{ticket?.id || '-'} - Call ID {ticket?.retell_call_id || '-'}
            </div>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <Field label='Caller name'>{ticket?.caller_name || '-'}</Field>
          <Field label='Caller phone'>{formatPhone(ticket?.caller_phone)}</Field>
          <Field label='Callback number'>{formatPhone(ticket?.callback_number)}</Field>
          <Field label='Issue category'>{ticket?.issue_category || '-'}</Field>
          <Field label='Priority'>{ticket?.priority || '-'}</Field>
          <Field label='Preferred follow-up'>
            {ticket?.preferred_follow_up_method || '-'}
          </Field>
          <Field label='Location'>
            {ticket?.location?.name || ticket?.location_name || ticket?.location_id || '-'}
          </Field>
          <Field label='Assignee'>
            <PersonName
              avatar={getPersonAvatar(ticket?.assignee)}
              name={getPersonDisplayName(ticket?.assignee)}
            />
          </Field>
          <Field label='Created'>{formatDate(ticket?.created_at)}</Field>
          <Field label='Updated'>{formatDate(ticket?.updated_at)}</Field>
          <Field label='Recording'>
            <RecordingPlayer url={ticket?.recording_url} />
          </Field>
          <Field label='Resolution status'>
            {ticket?.resolution_status || '-'}
          </Field>
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <TextBlock label='Customer sentiment' value={ticket?.customer_sentiment} />
          <TextBlock label='Call summary' value={ticket?.call_summary} />
          <TextBlock
            label='Collected information'
            value={ticket?.collected_information}
          />
          <TextBlock label='Missing information' value={ticket?.missing_information} />
        </div>

        <div className='border-t border-dark-100 pt-8'>
          <div className='text-base text-light-700 font-bold mb-4'>Edit AI call</div>
          <div className='grid grid-cols-2 gap-4 mb-4'>
            <Select
              label='Status'
              value={form.status}
              options={AI_CALL_EDIT_STATUS_OPTIONS}
              onChange={value => setForm({ ...form, status: value })}
            />
            <Select
              label='Assignee'
              value={form.assignee_crm_user_id}
              options={editAssigneeOptions}
              placeholder='Assignee'
              isPerson
              onChange={value =>
                setForm({ ...form, assignee_crm_user_id: value })
              }
            />
          </div>
          <ParagraphInput
            label='Note'
            name='note'
            value={form.note}
            maxLength={1000}
            onChange={e => setForm({ ...form, note: e.target.value })}
          />
        </div>

        <div className='border-t border-dark-100 pt-8'>
          <div className='text-base text-light-700 font-bold mb-4'>Operation logs</div>
          {loadingLogs ? (
            <div className='h-24 relative'>
              <PageLoading />
            </div>
          ) : logs.length ? (
            <div className='space-y-4'>
              {logs.map(log => (
                <div key={log.id} className='border border-dark-100 rounded-lg p-4'>
                  <div className='flex justify-between mb-3'>
                    <div className='text-sm text-light-700 font-bold capitalize'>
                      {log.action || '-'}
                    </div>
                    <div className='text-xs text-light-300'>
                      {formatDate(log.created_at)}
                    </div>
                  </div>
                  <div className='text-sm text-light-500 mb-3'>
                    {getPersonDisplayName(log.operator)}
                  </div>
                  <div className='space-y-2'>
                    {Object.keys(log.changes || {}).map(key => {
                      const change = log.changes[key]
                      return (
                        <div key={key} className='text-xs text-light-500'>
                          <span className='text-light-300'>
                            {formatLogField(key)}:{' '}
                          </span>
                          <span>{formatLogValue(key, change?.old)}</span>
                          <span className='text-light-300'> {'->'} </span>
                          <span className='text-light-700'>
                            {formatLogValue(key, change?.new)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-sm text-light-300'>No logs</div>
          )}
        </div>
      </div>
    </Drawer>
  )
}
